import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Loader2, UserPlus, ShieldAlert } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useT } from '../context/I18nContext';
import { toast } from 'sonner';

const MIN_AGE = 18;

const calcAge = (iso) => {
  if (!iso) return 0;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 0;
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
  return a;
};

const AccountRegister = () => {
  const { register } = useCustomerAuth();
  const { t } = useT();
  const [form, setForm] = useState({ name: '', email: '', phone: '', birthdate: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const returnTo = new URLSearchParams(loc.search).get('return') || '/checkout';

  const age = useMemo(() => calcAge(form.birthdate), [form.birthdate]);
  const ageBlocked = form.birthdate && age < MIN_AGE;

  const submit = async (e) => {
    e.preventDefault();
    if (ageBlocked) return toast.error(t('account.age_blocked'));
    if (!form.birthdate) return toast.error(t('account.birthdate_required'));
    if (form.password.length < 8) return toast.error(t('account.password_short'));
    setSubmitting(true);
    try {
      await register(form);
      toast.success(t('account.welcome'));
      nav(returnTo);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registration failed');
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <Header />
      <main className="bg-black min-h-[60vh]">
        <div className="max-w-md mx-auto px-6 py-16">
          <Card className="bg-zinc-950 border-zinc-900 p-8" data-testid="account-register-card">
            <div className="text-center mb-6">
              <UserPlus className="h-8 w-8 mx-auto mb-2 text-[#d4af37]" />
              <h1 className="serif-italic gold-gradient-text text-3xl">{t('account.register_title')}</h1>
              <p className="text-zinc-400 text-sm mt-2">{t('account.register_subtitle')}</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-zinc-300">{t('account.full_name')}</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-transparent border-zinc-800 mt-1" data-testid="account-register-name" />
              </div>
              <div>
                <Label className="text-zinc-300">{t('checkout.email')} <span className="text-rose-400">*</span></Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-transparent border-zinc-800 mt-1" data-testid="account-register-email" />
              </div>
              <div>
                <Label className="text-zinc-300">{t('checkout.phone')} <span className="text-rose-400">*</span></Label>
                <Input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-transparent border-zinc-800 mt-1" placeholder="+31 6 12 34 56 78" data-testid="account-register-phone" />
              </div>
              <div>
                <Label className="text-zinc-300">{t('account.birthdate')} <span className="text-rose-400">*</span></Label>
                <Input required type="date" value={form.birthdate} onChange={(e) => setForm({ ...form, birthdate: e.target.value })} className="bg-transparent border-zinc-800 mt-1 [color-scheme:dark]" data-testid="account-register-birthdate" />
                {ageBlocked && (
                  <p className="text-rose-400 text-xs mt-1 flex items-center gap-1.5" data-testid="account-age-blocked">
                    <ShieldAlert className="h-3.5 w-3.5" /> {t('account.age_blocked')}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-zinc-300">{t('account.password')} <span className="text-zinc-500 text-xs">({t('account.min_8')})</span></Label>
                <Input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-transparent border-zinc-800 mt-1" data-testid="account-register-password" />
              </div>
              <Button type="submit" disabled={submitting || ageBlocked} className="btn-gold w-full h-11" data-testid="account-register-submit">
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />…</> : t('account.create_account')}
              </Button>
            </form>
            <p className="text-zinc-500 text-sm text-center mt-5">
              {t('account.have_account')}{' '}
              <Link to={`/login?return=${encodeURIComponent(returnTo)}`} className="text-[#d4af37] underline" data-testid="account-goto-login">{t('account.sign_in')}</Link>
            </p>
          </Card>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default AccountRegister;
